import type { ChatMessage } from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import {
  ChatMessagePayloadType,
  CustomChatMessagePayloadType,
} from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import type { Chat } from '@/contexts/chat/scripts/hooks/useChats/types';
import GroupCreateMessage from '@/pages/messages/components/chat/chat-main/components/chat-messages/components/group/groupCreateMessage';
import GroupInviteMessage from '@/pages/messages/components/chat/chat-main/components/chat-messages/components/group/groupInviteMessage';
import GroupRemoveMemberMessage from '@/pages/messages/components/chat/chat-main/components/chat-messages/components/group/groupRemoveMemberMessage';
import GroupUpdateMessage from '@/pages/messages/components/chat/chat-main/components/chat-messages/components/group/groupUpdateMessage';
import SysText from '@/pages/messages/components/chat/chat-main/components/chat-messages/components/sysText';
import CallMessage from '@/pages/messages/components/chat/chat-main/components/chat-messages/components/media/callMessage';
import DestroyExpireDuration from '@/pages/messages/components/chat/chat-main/components/chat-messages/components/destoryExpireDuration';
import { safeJson } from '@/scripts/utils';
import useMessageExpireTimer from '@/scripts/hooks/useMessageExpireTimer';
import { ParticleEffectEnum } from '@/components/particle-effect-item';
import VoiceCallText from '../voiceCall';

interface LastMessageRendererProps {
  message?: ChatMessage;
  chat: Chat;
  setLastMessage: any;
}

function MessageWithExpireTime({
  message,
  chat,
  children,
  setLastMessage,
}: LastMessageRendererProps & { children: React.ReactNode }) {
  useMessageExpireTimer({
    message: message!,
    chat,
    doExpireAnimation: () => {
      return new Promise((resolve) => {
        const timerId = setTimeout(() => {
          resolve(ParticleEffectEnum.Duration);
          clearTimeout(timerId);
        }, ParticleEffectEnum.Duration);
      });
    },
    onExpire: () => {
      setLastMessage(undefined);
    },
  });
  return children;
}

const wrapWithExpireTime = (
  props: LastMessageRendererProps,
  content: React.ReactNode
) => {
  if (!props.message) return null;
  return <MessageWithExpireTime {...props}>{content}</MessageWithExpireTime>;
};

const LastMessageRenderer = (props: LastMessageRendererProps) => {
  const { message, chat } = props;

  const node = useMemo(() => {
    switch (message?.payload_type) {
      case CustomChatMessagePayloadType.ADD_TO_CONTACTS: {
        const { name } = safeJson(message.payload, { name: '' });
        return (
          <SysText noStyle>
            You have added {name}, and now you can start chatting
          </SysText>
        );
      }

      case ChatMessagePayloadType.TEXT:
      case ChatMessagePayloadType.ENCRYPTED_TEXT:
        return <SysText noStyle>{message.payload}</SysText>;

      case ChatMessagePayloadType.IMAGE: {
        return <SysText noStyle>[Photo]</SysText>;
      }

      case ChatMessagePayloadType.FILE: {
        const { name } = safeJson(message.payload, {} as any);
        return <SysText noStyle>[File]{name}</SysText>;
      }

      case ChatMessagePayloadType.GROUP_CREATE:
        return <GroupCreateMessage payload={message.payload} noStyle />;

      case ChatMessagePayloadType.GROUP_UPDATE:
        return <GroupUpdateMessage payload={message.payload} noStyle />;

      case ChatMessagePayloadType.GROUP_INVITE:
        return <GroupInviteMessage payload={message.payload} noStyle />;

      case ChatMessagePayloadType.GROUP_REMOVE_MEMBER:
        return <GroupRemoveMemberMessage payload={message.payload} noStyle />;

      case ChatMessagePayloadType.GROUP_DISBAND: {
        const { user_name } = safeJson(message.payload, { user_name: '' });
        return (
          <SysText noStyle>
            The group owner {user_name} has disbanded this group
          </SysText>
        );
      }

      case ChatMessagePayloadType.GROUP_MEMBER_QUIT: {
        const { user_name } = safeJson(message.payload, { user_name: '' });
        return <SysText noStyle>{user_name} has left the group</SysText>;
      }

      case ChatMessagePayloadType.MEDIA_AUDIO_CALL:
      case ChatMessagePayloadType.MEDIA_VIDEO_CALL: {
        return (
          <VoiceCallText
            status={message.status}
            payload_type={message.payload_type}
            message={message}
            chat={chat}
          >
            <CallMessage
              noStyle
              payload_type={message.payload_type}
              status={message.status}
              chat={chat}
              message={message}
              showIcon={false}
            />
          </VoiceCallText>
        );
      }

      case ChatMessagePayloadType.EXPIRE_DURATION:
        return <DestroyExpireDuration noStyle payload={message.payload} />;

      default:
        return message ? <span>{message.payload}</span> : null;
    }
  }, [chat, message]);

  return wrapWithExpireTime(props, node);
};

export default LastMessageRenderer;
